"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import { createPortal } from "react-dom";

import { SixPillarsPreviewSurface } from "@/components/site/sections/six-pillars-preview/preview-surface";
import { type SixPillarPreviewId } from "@/lib/six-pillars-preview";

type InteractivePreviewProps = {
  pillarId: SixPillarPreviewId;
  embedded?: boolean;
};

const SixPillarsPreviewModal = dynamic(
  () =>
    import("@/components/site/sections/six-pillars-preview/modal").then(
      (module) => module.SixPillarsPreviewModal,
    ),
  {
    ssr: false,
  },
);

export function SixPillarsInteractivePreview({
  pillarId,
  embedded = false,
}: InteractivePreviewProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      <SixPillarsPreviewSurface
        pillarId={pillarId}
        variant="compact"
        embedded={embedded}
        onOpenFullscreen={() => setIsOpen(true)}
      />

      {mounted && isOpen
        ? createPortal(
            <SixPillarsPreviewModal
              pillarId={pillarId}
              onClose={() => setIsOpen(false)}
            />,
            document.body,
          )
        : null}
    </>
  );
}
